import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Header from '../components/Header';
import Toast from '../components/Toast';
import { churchDirectoryService, ChurchDirectoryProfile, UpsertChurchDirectoryProfileData } from '../api/churchDirectoryService';
import { planterService } from '../api/planterService';

const emptyForm: UpsertChurchDirectoryProfileData = {
  churchName: '',
  contactName: '',
  contactPosition: '',
  contactPhone: '',
  contactEmail: '',
  city: '',
  state: '',
  addressStreet: '',
  addressStreet2: '',
  addressCity: '',
  addressState: '',
  addressZip: '',
  addressCountry: 'United States',
  denomination: '',
  website: '',
  notes: '',
};

const profileToForm = (
  profile: ChurchDirectoryProfile | null,
  planter: Awaited<ReturnType<typeof planterService.getMyPlanter>> | null,
): UpsertChurchDirectoryProfileData => {
  if (profile) {
    return {
      churchName: profile.churchName || '',
      contactName: profile.contactName || '',
      contactPosition: profile.contactPosition || '',
      contactPhone: profile.contactPhone || '',
      contactEmail: profile.contactEmail || '',
      city: profile.city || '',
      state: profile.state || '',
      addressStreet: profile.addressStreet || '',
      addressStreet2: profile.addressStreet2 || '',
      addressCity: profile.addressCity || '',
      addressState: profile.addressState || '',
      addressZip: profile.addressZip || '',
      addressCountry: profile.addressCountry || 'United States',
      denomination: profile.denomination || '',
      website: profile.website || '',
      notes: profile.notes || '',
    };
  }

  if (planter) {
    return {
      ...emptyForm,
      churchName: planter.churchName || '',
      contactName: planter.name || '',
      contactPhone: planter.phone || '',
      contactEmail: planter.email || '',
      city: planter.city || '',
      state: planter.state || '',
      addressCity: planter.city || '',
      addressState: planter.state || '',
    };
  }

  return emptyForm;
};

const ChurchProfile: React.FC = () => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<UpsertChurchDirectoryProfileData>(emptyForm);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

  const planterQuery = useQuery({
    queryKey: ['myPlanter'],
    queryFn: async () => {
      try {
        return await planterService.getMyPlanter();
      } catch (error: any) {
        if (error?.response?.status === 404) {
          return null;
        }
        throw error;
      }
    },
    retry: false,
  });

  const churchProfileQuery = useQuery({
    queryKey: ['myChurchProfile'],
    queryFn: async () => {
      try {
        return await churchDirectoryService.getMyChurchProfile();
      } catch (error: any) {
        if (error?.response?.status === 404) {
          return null;
        }
        throw error;
      }
    },
    enabled: !!planterQuery.data,
    retry: false,
  });

  const hasExistingProfile = !!churchProfileQuery.data;
  const isLoading = planterQuery.isLoading || (planterQuery.data ? churchProfileQuery.isLoading : false);
  const loadError = planterQuery.error || churchProfileQuery.error;

  useEffect(() => {
    if (hasInitialized || planterQuery.isLoading || churchProfileQuery.isLoading) {
      return;
    }

    setFormData(profileToForm(churchProfileQuery.data ?? null, planterQuery.data ?? null));
    setHasInitialized(true);
  }, [
    churchProfileQuery.data,
    churchProfileQuery.isLoading,
    hasInitialized,
    planterQuery.data,
    planterQuery.isLoading,
  ]);

  const mutation = useMutation({
    mutationFn: (data: UpsertChurchDirectoryProfileData) => churchDirectoryService.upsertMyChurchProfile(data),
    onSuccess: (savedProfile) => {
      queryClient.setQueryData(['myChurchProfile'], savedProfile);
      setFormData(profileToForm(savedProfile, planterQuery.data ?? null));
      setToast({
        message: hasExistingProfile ? 'Church profile updated.' : 'Church profile created.',
        type: 'success',
      });
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || 'Unable to save the church profile.';
      setToast({ message, type: 'error' });
    },
  });

  const statusCopy = useMemo(() => {
    if (!planterQuery.data) {
      return {
        title: 'Church Profile Unavailable',
        description: 'This account is not linked to a promoted planter record yet.',
      };
    }

    if (hasExistingProfile) {
      return {
        title: 'Church Profile',
        description: 'Update the church information that hub users see in the directory.',
      };
    }

    return {
      title: 'Complete Church Profile',
      description: 'Add the directory details hub users should see for your church plant.',
    };
  }, [hasExistingProfile, planterQuery.data]);

  const handleChange = (field: keyof UpsertChurchDirectoryProfileData, value: string) => {
    setFormData((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    mutation.mutate(formData);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      <div className="max-w-4xl mx-auto py-6 sm:py-8 px-4 sm:px-6 lg:px-8">
        <div className="space-y-6">
          <div className="bg-white shadow-sm rounded-xl p-4 sm:p-6">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{statusCopy.title}</h1>
            <p className="text-gray-600 mt-2">{statusCopy.description}</p>
          </div>

          {isLoading ? (
            <div className="bg-white shadow-lg rounded-xl p-8 text-center text-gray-500">
              Loading church profile...
            </div>
          ) : loadError ? (
            <div className="bg-red-50 border border-red-200 shadow-sm rounded-xl p-4 sm:p-6 text-red-700">
              {(loadError as any)?.response?.data?.message || 'Unable to load the church profile.'}
            </div>
          ) : !planterQuery.data ? (
            <div className="bg-yellow-50 border border-yellow-200 shadow-sm rounded-xl p-4 sm:p-6 text-yellow-800">
              Your account does not have an active planter profile yet. This page will unlock after promotion.
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-white shadow-lg rounded-xl p-4 sm:p-6 space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Church Name</label>
                  <input
                    type="text"
                    value={formData.churchName || ''}
                    onChange={(event) => handleChange('churchName', event.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Denomination</label>
                  <input
                    type="text"
                    value={formData.denomination || ''}
                    onChange={(event) => handleChange('denomination', event.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                  <input
                    type="url"
                    value={formData.website || ''}
                    onChange={(event) => handleChange('website', event.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="https://example.org"
                  />
                </div>
              </div>

              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Directory Contact</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name</label>
                    <input
                      type="text"
                      value={formData.contactName || ''}
                      onChange={(event) => handleChange('contactName', event.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Position / Title</label>
                    <input
                      type="text"
                      value={formData.contactPosition || ''}
                      onChange={(event) => handleChange('contactPosition', event.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email</label>
                    <input
                      type="email"
                      value={formData.contactEmail || ''}
                      onChange={(event) => handleChange('contactEmail', event.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contact Phone</label>
                    <input
                      type="tel"
                      value={formData.contactPhone || ''}
                      onChange={(event) => handleChange('contactPhone', event.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Location</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <input
                      type="text"
                      value={formData.city || ''}
                      onChange={(event) => handleChange('city', event.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                    <input
                      type="text"
                      value={formData.state || ''}
                      onChange={(event) => handleChange('state', event.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
                    <input
                      type="text"
                      value={formData.addressStreet || ''}
                      onChange={(event) => handleChange('addressStreet', event.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 2</label>
                    <input
                      type="text"
                      value={formData.addressStreet2 || ''}
                      onChange={(event) => handleChange('addressStreet2', event.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address City</label>
                    <input
                      type="text"
                      value={formData.addressCity || ''}
                      onChange={(event) => handleChange('addressCity', event.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address State</label>
                    <input
                      type="text"
                      value={formData.addressState || ''}
                      onChange={(event) => handleChange('addressState', event.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ZIP Code</label>
                    <input
                      type="text"
                      value={formData.addressZip || ''}
                      onChange={(event) => handleChange('addressZip', event.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                    <input
                      type="text"
                      value={formData.addressCountry || ''}
                      onChange={(event) => handleChange('addressCountry', event.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={formData.notes || ''}
                  onChange={(event) => handleChange('notes', event.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm min-h-[120px] focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={mutation.isPending}
                  className="inline-flex items-center px-4 py-2 rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {mutation.isPending ? 'Saving...' : hasExistingProfile ? 'Save Changes' : 'Create Profile'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChurchProfile;
